import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OrdersTable, PickList } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { PickOrder, PickItem, PickListData } from './pickPackTypes';
import { useOrdersStore } from '../../../store/ordersStore';
import { Order, listOrders, completePicking, confirmOrder, getPickDetail, PickDetailLine } from '../../../services/ordersApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './WarehousePickPack.module.css';

function adapt(o: Order): PickOrder {
  return {
    id: String(o.id),
    orderNumber: formatOrderId(o.id),
    customerName: o.customer_name || `Customer #${o.customer_id}`,
    priority: 'MEDIUM',
    itemsCount: o.items?.length ?? 0,
    status: o.status === 'shipped' ? 'Packed' : o.status === 'confirmed' ? 'Picking' : 'Pending',
  };
}

/**
 * Builds the pick list, preferring the real FEFO-allocated lot + location
 * per line from GET /orders/:id/pick-detail (VP-WMS-007) — a SKU with stock
 * split across lots surfaces as one row per lot/location, matching what the
 * backend actually reserved. Falls back to order.items with placeholder
 * '—' lot/location only if pick-detail came back empty (e.g. an order with
 * no live reservations), so the panel never goes blank.
 */
function buildPickList(o: Order | null, pickDetail: PickDetailLine[]): PickListData {
  if (!o) {
    return { orderNumber: '', customerName: '', priority: 'MEDIUM', items: [] };
  }
  const items: PickItem[] = pickDetail.length > 0
    ? pickDetail.map((line, i) => ({
        id: `${line.lot_id}-${line.sku_id}-${i}`,
        productName: line.sku_name || `SKU ${line.sku_id}`,
        sku: line.sku_code || String(line.sku_id),
        warehouseLocation: line.location_code || '—',
        lotNumber: line.lot_number || '—',
        requiredQty: `${line.quantity}`,
        pickedQty: '',
        completed: false,
      }))
    : (o.items ?? []).map((it) => ({
        id: String(it.id ?? it.sku_id),
        productName: it.product_name || `SKU ${it.sku_id}`,
        sku: it.sku_code || String(it.sku_id),
        warehouseLocation: '—',
        lotNumber: '—',
        requiredQty: `${it.quantity}`,
        pickedQty: '',
        completed: false,
      }));
  return {
    orderNumber: formatOrderId(o.id),
    customerName: o.customer_name || `Customer #${o.customer_id}`,
    priority: 'MEDIUM',
    items,
  };
}

const WarehousePickPack: React.FC = () => {
  const current = useOrdersStore((s) => s.current);
  const fetchOne = useOrdersStore((s) => s.fetchOne);
  const clearCurrent = useOrdersStore((s) => s.clearCurrent);

  // The pick queue is every order awaiting warehouse action: pending (needs
  // confirm) + confirmed (ready to pick). Fetched directly so we get both
  // statuses regardless of the shared orders-store single-status filter.
  const [queue, setQueue] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pickDetail, setPickDetail] = useState<PickDetailLine[]>([]);
  const [pickList, setPickList] = useState<PickListData>(buildPickList(null, []));
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pending, confirmedList] = await Promise.all([
        listOrders({ status: 'pending', limit: 100 }),
        listOrders({ status: 'confirmed', limit: 100 }),
      ]);
      setQueue([...pending.data, ...confirmedList.data]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Fetch the real FEFO lot + location allocation whenever the selected order
  // changes; rebuild the visible pick list once it lands (or clear it first
  // so a stale prior order's lots never leak into the new selection).
  useEffect(() => {
    let cancelled = false;
    if (!current) { setPickDetail([]); return; }
    setPickList(buildPickList(current, []));
    getPickDetail(current.id)
      .then((lines) => { if (!cancelled) setPickDetail(lines); })
      .catch(() => { if (!cancelled) setPickDetail([]); });
    return () => { cancelled = true; };
  }, [current]);

  useEffect(() => {
    setPickList(buildPickList(current, pickDetail));
  }, [current, pickDetail]);

  const orders = useMemo<PickOrder[]>(() => queue.map(adapt), [queue]);

  const handleSelectOrder = async (order: PickOrder) => {
    await fetchOne(order.id);
  };

  const handlePickedChange = (id: string, value: string) => {
    setPickList((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.id === id ? { ...item, pickedQty: value } : item)),
    }));
  };

  const handleCompleteToggle = (id: string) => {
    setPickList((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.id === id ? { ...item, completed: !item.completed } : item)),
    }));
  };

  const errMsg = (err: any, fallback: string) =>
    err?.error?.message || err?.response?.data?.error?.message || err?.message || fallback;

  // Pending → confirmed. Keep the panel open and refresh the selected order so
  // it flips to the "Complete Pick & Pack" action.
  const handleConfirmOrder = async () => {
    if (!current || confirming) return;
    setConfirming(true);
    setConfirmError(null);
    try {
      await confirmOrder(current.id);
      await fetchOne(current.id);
      await loadQueue();
    } catch (err: any) {
      setConfirmError(errMsg(err, 'Failed to confirm order'));
    } finally {
      setConfirming(false);
    }
  };

  // Confirmed → shipped (pick-pack complete). Close the panel and refresh.
  const handleConfirm = async () => {
    if (!current || confirming) return;
    setConfirming(true);
    setConfirmError(null);
    try {
      await completePicking(current.id);
      clearCurrent();
      await loadQueue();
    } catch (err: any) {
      setConfirmError(errMsg(err, 'Failed to confirm picking'));
    } finally {
      setConfirming(false);
    }
  };
  const handleCancel = () => { clearCurrent(); };

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && queue.length === 0}
        error={error}
        empty={!loading && orders.length === 0}
        emptyMessage="No orders to pick"
        onRetry={() => loadQueue()}
      >
        <OrdersTable orders={orders} onSelectOrder={handleSelectOrder} />
      </AsyncBoundary>
      {current && (
        <>
          <PickList
            pickList={pickList}
            onPickedChange={handlePickedChange}
            onCompleteToggle={handleCompleteToggle}
            onConfirm={handleConfirm}
            onConfirmOrder={handleConfirmOrder}
            confirmed={current.status === 'confirmed'}
            busy={confirming}
            onCancel={handleCancel}
          />
          {confirming && <div style={{ padding: 8, color: '#64748B' }}>Working…</div>}
          {confirmError && (
            <div style={{ padding: 8, color: '#DC2626' }}>{confirmError}</div>
          )}
        </>
      )}
    </div>
  );
};

export const WarehousePickPackPage: React.FC = () => <WarehousePickPack />;
export default WarehousePickPack;
